import { useCallback, useEffect, useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Spin, Tabs, message as antMessage } from "antd";
import { serverFetch } from "@core/server";
import DialogMain from "./dialog-main";
import Messages from "./messages";
import "./dialog-detail.css";

const errMsg = (e) => e?.message || "Ошибка";

const DialogModal = ({
  id,
  onClose,
  messages,
  setMessages,
  messagesLoading,
  messagesEndRef,
  scrollToBottom,
  onSent
}) => {
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [activeTab, setActiveTab] = useState("messages");

  const reloadPayload = useCallback(() => {
    if (!id) return;
    serverFetch(`Message/Dialog/${id}`)
      .then((data) => setDialog(data))
      .catch((e) => antMessage.error(errMsg(e)));
  }, [id]);

  const handleDialogRead = useCallback((readUpToMessageId, syncLastMessageId = false) => {
    setDialog((prev) => {
      if (!prev) return prev;
      let nextReadId;
      if (readUpToMessageId != null) {
        const currentRead = prev.lastMessageIdIsRead;
        if (currentRead != null && Number(currentRead) >= Number(readUpToMessageId)) {
          return prev;
        }
        nextReadId = readUpToMessageId;
      } else {
        nextReadId = prev.lastMessageId;
      }
      return {
        ...prev,
        lastMessageIdIsRead: nextReadId,
        ...(syncLastMessageId && readUpToMessageId != null ? { lastMessageId: readUpToMessageId } : {})
      };
    });
  }, []);

  const handleDialogUnread = useCallback((readUpToMessageId) => {
    setDialog((prev) => {
      if (!prev) return prev;
      return { ...prev, lastMessageIdIsRead: readUpToMessageId ?? null };
    });
  }, []);

  const handleTabChange = useCallback(
    (key) => {
      setActiveTab(key);
      if (key === "messages") setTimeout(scrollToBottom, 0);
    },
    [scrollToBottom]
  );

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setDialog(null);
    setActiveTab("messages");
    serverFetch(`Message/Dialog/${id}`)
      .then((data) => setDialog(data))
      .catch((e) => {
        antMessage.error(errMsg(e));
        onClose(false);
      })
      .finally(() => setLoading(false));
  }, [id, onClose]);

  if (!id) return null;

  const headerTitle =
    (dialog?.predName != null && String(dialog.predName).trim()) ||
    (dialog?.enteredName != null && String(dialog.enteredName).trim()) ||
    (dialog?.name != null && String(dialog.name).trim()) ||
    (!dialog ? "Диалог" : `Обращение #${dialog.id ?? id}`);

  return (
    <div className="dialog-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose(false)}>
      <div className="dialog-overlay__panel" onMouseDown={(e) => e.stopPropagation()}>
        <div className="dialog-overlay__title">
          <span>{headerTitle}</span>
          <CloseOutlined className="dialog-overlay__close" onClick={() => onClose(false)} />
        </div>
        <div className="dialog-overlay__body">
          {!dialog ? (
            <Spin spinning={loading}>
              <div className="dialog-modal-loading-placeholder" />
            </Spin>
          ) : (
            <Tabs
              type="card"
              className="dialog-modal-tabs"
              activeKey={activeTab}
              onChange={handleTabChange}
              destroyOnHidden={false}
              items={[
                {
                  key: "messages",
                  label: "Сообщения",
                  children: (
                    <Messages
                      dialog={dialog}
                      messages={messages}
                      messagesLoading={messagesLoading}
                      messagesEndRef={messagesEndRef}
                      scrollToBottom={scrollToBottom}
                      setMessages={setMessages}
                      onSent={onSent}
                      onDialogRead={handleDialogRead}
                      onDialogUnread={handleDialogUnread}
                    />
                  )
                },
                {
                  key: "dialog",
                  label: "Диалог",
                  children: <DialogMain dialog={dialog} onReload={reloadPayload} onClose={() => onClose(false)} />
                }
              ]}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DialogModal;
